import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { SortingState, ColumnFiltersState } from '@tanstack/react-table';
import { Alert, Box, Stack } from '@mui/material';

import { useFetchCampaigns } from '@/hooks';
import { TABLE_PAGINATION } from '@/constants';
import { FilterableDataTable, useUrlPagination, useUrlSearch } from '@/components/common/FilterableDataTable';
import SearchInput from '@/components/common/SearchInput';
import campaignListColumns from './campaignListColumns';

import type { Campaign } from './types';

interface CampaignListProps {
    campaigns?: Campaign[];
    isFirstLoad?: boolean;
    isLoadingNewPage?: boolean;
    onError?: (hasError: boolean) => void;
}

/**
 * Campaign list component that displays fetched campaigns data in a sortable table.
 */
const CampaignList: React.FC<CampaignListProps> = ({ campaigns = [], onError }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { pagination, setPagination } = useUrlPagination();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const { search, setSearch, clearSearch } = useUrlSearch();

    // Fetch campaigns data
    const {
        data: campaignsData,
        isLoading: isLoadingCampaigns,
        isError: isCampaignsError,
        error: campaignsError,
    } = useFetchCampaigns({ sorting, pagination, search });

    const handleRowClick = useCallback(
        (campaign: Campaign) => {
            navigate(`/projects/${campaign.id}`, {
                state: { returnUrl: `${location.pathname}${location.search}` },
            });
        },
        [navigate, location.pathname, location.search]
    );

    useEffect(() => {
        onError?.(isCampaignsError);
    }, [isCampaignsError, onError]);

    if (isCampaignsError) {
        return (
            <Stack spacing={0.5}>
                <Alert severity="error" elevation={1}>
                    Error loading projects data ({campaignsError?.message})
                </Alert>
            </Stack>
        );
    }

    const tableData = campaigns || campaignsData?.data || [];
    const tableDataObject = campaignsData ?? {
        data: tableData,
        total: 0,
        totalFiltered: 0,
        limit: pagination.pageSize,
        offset: 0,
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <SearchInput defaultValue={search} placeholder="Search projects..." onChange={setSearch} onClear={clearSearch} />
            </Box>
            <FilterableDataTable<Campaign>
                data={tableDataObject}
                isLoading={isLoadingCampaigns}
                columns={campaignListColumns}
                filters={[]}
                initialFilters={columnFilters}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                onFiltersChange={setColumnFilters}
                onRowClick={handleRowClick}
                noDataMessage="No projects found"
                defaultRowsPerPage={TABLE_PAGINATION.DEFAULT_ROWS_PER_PAGE}
                rowsPerPageOptions={TABLE_PAGINATION.ROWS_PER_PAGE_OPTIONS}
                persistFilters={true}
                storageKey="campaign-list"
            />
        </>
    );
};

export default CampaignList;
